import { useState, type FormEvent } from "react";
import { heroImages } from "@/mocks/siteData";

const FORM_URL = "https://readdy.ai/api/form/daeiskptto0ceqqngu50";

const benefits = [
  { icon: "ri-mail-send-line", text: "重要反转与回应，第一时间推送到邮箱" },
  { icon: "ri-timer-flash-line", text: "只发关键节点，不刷屏不打扰" },
  { icon: "ri-shield-check-line", text: "随时可退订，隐私不滥用" },
];

export default function Subscribe() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    const honeypot = String(fd.get("website_alt") ?? "").trim();
    const email = String(fd.get("email") ?? "").trim();

    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }

    if (!email) {
      setStatus("error");
      setFormError("请填写用于接收追更提醒的邮箱地址。");
      return;
    }

    setStatus("submitting");
    setFormError("");

    const params = new URLSearchParams();
    params.set("email", email);

    try {
      const res = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const code = parsed?.code ?? "";
      const serverMsg =
        parsed?.meta?.message || parsed?.meta?.detail || (res.ok ? "" : "提交失败，请稍后再试。");
      const isSpam = /spam|form data is spam/i.test(responseText + serverMsg);

      if (res.ok && code === "OK" && !isSpam) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(isSpam ? "提交过于频繁，请稍后再试。" : serverMsg || "提交失败，请稍后再试。");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，请检查连接后再试。");
    }
  };

  return (
    <section id="subscribe" className="scroll-mt-20 bg-background-50">
      <div className="mx-auto w-full max-w-7xl px-4 py-16 md:px-6 md:py-24">
        <div className="relative overflow-hidden rounded-3xl bg-background-950 text-background-50">
          <img
            src={heroImages.subscribe}
            alt="订阅追更提醒的雷达信号主题宽幅插画"
            className="absolute inset-0 h-full w-full object-cover opacity-45"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/45 to-black/30" aria-hidden="true" />

          <div className="relative grid w-full grid-cols-1 gap-8 px-6 py-12 md:px-12 md:py-16 lg:grid-cols-2 lg:gap-12">
            <div className="flex flex-col justify-center">
              <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-accent-300">
                <i className="ri-notification-4-line text-base" />
                追更提醒
              </span>
              <h2 className="mt-3 font-heading text-2xl font-black leading-snug md:text-4xl">
                蹲更新，留个邮箱
                <br />
                <span className="font-accent font-normal text-accent-400">我们不让你错过下一集</span>
              </h2>
              <ul className="mt-6 space-y-3">
                {benefits.map((b) => (
                  <li key={b.text} className="flex items-center gap-3 text-sm text-background-100/90">
                    <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-accent-500/20 text-accent-300">
                      <i className={`${b.icon} text-base`} />
                    </span>
                    {b.text}
                  </li>
                ))}
              </ul>
            </div>

            <div className="flex items-center justify-center">
              <form
                id="subscribe-form"
                data-readdy-form="52baoliao-subscribe"
                onSubmit={onSubmit}
                noValidate
                className="w-full max-w-md rounded-2xl border border-white/15 bg-background-50 p-6 text-foreground-900 shadow-lift"
              >
                <label htmlFor="subscribe-email" className="text-sm font-bold text-foreground-950">
                  订阅更新通知
                </label>
                <p className="mt-1 text-xs text-foreground-500">
                  留下邮箱，重大进展与新一集出现时通知你（我们不会发广告）。
                </p>

                <input
                  type="email"
                  id="subscribe-email"
                  name="email"
                  required
                  placeholder="name@example.com"
                  className="mt-4 w-full rounded-md border border-background-300 bg-background-100 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                />

                <div className="hp-guard" aria-hidden="true">
                  <label>
                    网站备用联系方式
                    <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" readOnly />
                  </label>
                </div>

                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="mt-4 inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {status === "submitting" ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" />
                      正在提交…
                    </>
                  ) : (
                    <>
                      开启追更提醒
                      <i className="ri-arrow-right-line" />
                    </>
                  )}
                </button>

                {status === "success" ? (
                  <p className="mt-3 flex items-start gap-2 rounded-md bg-secondary-100 px-3 py-2.5 text-xs font-medium text-secondary-800">
                    <i className="ri-checkbox-circle-fill text-base" />
                    订阅成功！后续有新进展时，我们会第一时间通过邮箱提醒你。
                  </p>
                ) : null}

                {status === "error" && formError ? (
                  <p className="mt-3 flex items-start gap-2 rounded-md bg-primary-100 px-3 py-2.5 text-xs font-medium text-primary-700">
                    <i className="ri-error-warning-fill text-base" />
                    {formError}
                  </p>
                ) : null}
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}